import { useEffect, useState, useRef } from "react";
import { db } from "../utils/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import imageCompression from "browser-image-compression";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import QrDrawer from "../components/QrGenerator/QrDrawer";
import WorkingHoursDrawer from "../components/MenuEditor/WorkingHoursDrawer"
import StaffDrawer from "../components/MenuEditor/StaffDrawer"
import { HiOutlineTrash, HiOutlinePhotograph, HiOutlineLink, HiOutlineExternalLink } from "react-icons/hi";
import { BiDotsVerticalRounded } from "react-icons/bi";
import trackEvent from '../utils/analytics';

import {
  FaPlus,
  FaSave,
  FaSyncAlt,
  FaEye,
  FaQrcode,
  FaClock,
  FaUsers,
} from "react-icons/fa";


export default function MenuEditor() {

  const EMPTY_DESC_EDITOR = {
    open: false,
    lang: null,
    idx: null,
    value: "",
  };

  const editorRef = useRef(null);
  const email = localStorage.getItem("user");
  const [userDetails, setUserDetails] = useState(null);

  const [menu, setMenu] = useState({ englishMenu: [], hindiMenu: [] });
  const [activeTab, setActiveTab] = useState("english");
  const [loading, setLoading] = useState(true);
  const [slug, setSlug] = useState("");
  //const [hasHindiMenu, setHasHindiMenu] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const DRAFT_KEY = `menu-draft-${email}`;
  const [showSummary, setShowSummary] = useState(false);
  const [openQrDrawer, setOpenQrDrawer] = useState(false);

  const [descEditor, setDescEditor] = useState(EMPTY_DESC_EDITOR);
  const [openWorkingHoursDrawer, setOpenWorkingHoursDrawer] = useState(false);
  const [showStaffDrawer, setShowStaffDrawer] = useState(false);

  const openDescriptionEditor = (lang, idx) => {
    setDescEditor({
      open: true,
      lang,
      idx,
      value: menu[`${lang}Menu`][idx]?.["Item Description"] || "",
    });
  };

  useEffect(() => {
    if (descEditor.open && editorRef.current) {
      editorRef.current.innerHTML = descEditor.value || "";
      editorRef.current.focus();
    }
  }, [descEditor.open]);




  const getEmptyDish = () => ({
    "Item Name": "",
    "Item Description": "",
    "Level 1": "",
    "Level 2": "",
    "Price": "",
    "Veg_NonVeg": "",
    imageUrl: "",

    // ✅ NEW FIELDS
    itemCode: "",
    categoryCode: "",
    available: true,
  });


  const storage = getStorage();

  const createSlug = (name) =>
    name.toLowerCase().trim().replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, "-");

  useEffect(() => {
    if (!isDirty) return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify(menu));
  }, [menu, isDirty]);


  useEffect(() => {
    if (!email) return;

    const fetchMenu = async () => {
      try {
        const userRef = doc(db, "userDetails", email);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) return;

        const userData = userSnap.data();
        setUserDetails(userData); // ✅ NEW

        const businessName = userSnap.data().businessName;
        const menuSlug = createSlug(businessName);
        setSlug(menuSlug);

        const menuRef = doc(db, "menus", menuSlug);
        const menuSnap = await getDoc(menuRef);

        if (menuSnap.exists()) {
          const data = menuSnap.data();
          const normalizeItems = (items = []) =>
            items.map((item) => ({
              ...item,
              itemCode: item.itemCode || "",
              categoryCode: item.categoryCode || "",
              available: item.available === false ? false : true,
            }));

          setMenu({
            englishMenu: normalizeItems(data.englishMenu),
            hindiMenu: normalizeItems(data.hindiMenu),
          });
          //setHasHindiMenu(!!data.hindiMenu);

          const draft = localStorage.getItem(DRAFT_KEY);
          if (draft) {
            const parsed = JSON.parse(draft);
            setMenu({
              englishMenu: normalizeItems(parsed.englishMenu),
              hindiMenu: normalizeItems(parsed.hindiMenu),
            });
            setIsDirty(true);
          }

          setOriginalMenu(JSON.parse(JSON.stringify({
            englishMenu: normalizeItems(data.englishMenu),
            hindiMenu: normalizeItems(data.hindiMenu),
          })));


        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, [email]);

  const [originalMenu, setOriginalMenu] = useState(null);



  const handleChange = (lang, idx, field, value) => {
    const updated = [...menu[`${lang}Menu`]];
    updated[idx][field] = value;
    setMenu({ ...menu, [`${lang}Menu`]: updated });
    setIsDirty(true);

  };

  const addDishRow = (lang) => {
    const updated = [...menu[`${lang}Menu`], getEmptyDish()];
    setMenu({ ...menu, [`${lang}Menu`]: updated });
    setIsDirty(true);
  };

  const deleteDishRow = (lang, idx) => {
    if (!window.confirm("Delete this item?")) return;

    const updated = [...menu[`${lang}Menu`]];
    updated.splice(idx, 1);
    setMenu({ ...menu, [`${lang}Menu`]: updated });
    setIsDirty(true);
  };


  const generateSummary = () => {
    const changes = [];

    menu.englishMenu.forEach((item, i) => {
      const old = originalMenu?.englishMenu[i];
      if (!old) {
        changes.push(`🆕 New dish added: ${item["Item Name"]}`);
        return;
      }

      if (old.Price !== item.Price) {
        changes.push(`✏ ${item["Item Name"]} price ₹${old.Price} → ₹${item.Price}`);
      }

      if (old.imageUrl !== item.imageUrl) {
        changes.push(`🖼 Image updated for ${item["Item Name"]}`);
      }

      if (originalMenu?.englishMenu.length > menu.englishMenu.length) {
        changes.push("❌ Some dishes were removed");
      }

      if (old.itemCode !== item.itemCode) {
        changes.push(`🔢 ${item["Item Name"]} itemCode changed`);
      }

      if (old.categoryCode !== item.categoryCode) {
        changes.push(`📂 ${item["Item Name"]} categoryCode changed`);
      }

      if (old.available !== item.available) {
        changes.push(
          `${item.available ? "🟢 Enabled" : "🔴 Disabled"} ${item["Item Name"]}`
        );
      }

    });



    return changes;
  };


  const handleImageUpload = async (file, lang, idx, dishName) => {
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      alert("Only PNG / JPG images allowed");
      return;
    }

    const compressed = await imageCompression(file, {
      maxSizeMB: 0.03,          // ✅ 30 KB (4x clearer than now)
      maxWidthOrHeight: 320,    // ✅ future-proof for popup usage
      fileType: "image/webp",
      initialQuality: 0.75,     // ✅ important
      useWebWorker: true,
    });

    const safeName = dishName.toLowerCase().replace(/[^a-z0-9]/g, "-");
    const imageRef = ref(storage, `menu-thumbnails/${slug}/${safeName}.webp`);

    await uploadBytes(imageRef, compressed);
    const url = await getDownloadURL(imageRef);

    const updated = [...menu[`${lang}Menu`]];
    updated[idx].imageUrl = url;
    setMenu({ ...menu, [`${lang}Menu`]: updated });
    setIsDirty(true);
  };

  const syncEnglishImagesToHindi = () => {
    if (!menu.hindiMenu.length) return alert("Indian language menu not available");

    const updatedHindi = menu.hindiMenu.map((item, i) => ({
      ...item,
      imageUrl: menu.englishMenu[i]?.imageUrl || "",
    }));

    setMenu({ ...menu, hindiMenu: updatedHindi });
    setIsDirty(true);
  };

  const handleSaveMenu = async () => {
    try {
      const ref = doc(db, "menus", slug);

      const payload = {
        email,
        englishMenu: menu.englishMenu,
      };

      // Only write hindiMenu if user actually added something
      if (menu.hindiMenu.length > 0) {
        payload.hindiMenu = menu.hindiMenu;
      }

      await updateDoc(ref, payload);

      localStorage.removeItem(DRAFT_KEY);
      setIsDirty(false);
      alert("Menu saved successfully");
      trackEvent("menu_updated", {business_slug: slug, user_type: "owner"});
    } catch (err) {
      console.error(err);
      alert("Error saving menu");
    }
  };






  const renderTable = (lang) => (
    <div className="overflow-hidden rounded-xl border border-zinc-200 shadow-sm bg-white">
      <table className="w-full text-left border-separate border-spacing-0">

        {/* --- Premium Header --- */}
        <thead>
          <tr className="bg-zinc-50/50">
            {[
              "#",
              "Item Name",
              "Item Code",
              "Category Code",
              "Available",
              "Item Description",
              "Level 1",
              "Level 2",
              "Price",
              "Category",
              "Media",
              ""
            ].map((h, i) => (
              <th
                key={h}
                className={`
                px-4 py-4 text-[11px] font-black uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-100
                ${i === 0 ? "text-center w-12" : ""}
                ${h === "Price" ? "text-right" : ""}
              `}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-zinc-100">
          {menu[`${lang}Menu`]?.map((item, idx) => (
            <tr
              key={idx}
              className={`
    group transition-colors duration-200
    ${item.available === false
                  ? "opacity-40 bg-red-50/20"
                  : "hover:bg-amber-50/30"}
  `}
            >
              {/* Index */}
              <td className="px-4 py-4 text-center text-xs font-bold text-zinc-400">
                {String(idx + 1).padStart(2, '0')}
              </td>

              {/* Item Name */}
              <td className="px-2 py-3">
                <input
                  value={item["Item Name"] || ""}
                  placeholder="Enter Item Name..."
                  onChange={(e) => handleChange(lang, idx, "Item Name", e.target.value)}
                  className="w-full bg-transparent border border-transparent hover:border-zinc-200 focus:border-amber-500 focus:bg-white focus:ring-4 focus:ring-amber-500/5 px-3 py-2 rounded-lg text-sm font-medium transition-all text-zinc-800"
                />
              </td>

              {/* Item Code */}
              <td className="px-2 py-3 w-24">
                <input
                  value={item.itemCode || ""}
                  placeholder="Code"
                  onChange={(e) => handleChange(lang, idx, "itemCode", e.target.value)}
                  className="w-full text-xs bg-zinc-50 border border-transparent hover:border-zinc-200 focus:border-[#6cbb23] px-2 py-1 rounded-md"
                />
              </td>

              {/* Category Code */}
              <td className="px-2 py-3 w-28">
                <input
                  value={item.categoryCode || ""}
                  placeholder="Cat Code"
                  onChange={(e) => handleChange(lang, idx, "categoryCode", e.target.value)}
                  className="w-full text-xs bg-zinc-50 border border-transparent hover:border-zinc-200 focus:border-[#6cbb23] px-2 py-1 rounded-md"
                />
              </td>

              {/* Available Toggle */}
              <td className="px-2 py-3 text-center w-24">
                <button
                  onClick={() =>
                    handleChange(lang, idx, "available", !item.available)
                  }
                  className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition
                  ${item.available === false ? "bg-gray-300" : "bg-[#6cbb23]"}
                `}
                >
                  <span
                    className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition
                    ${item.available === false ? "translate-x-1" : "translate-x-6"}
                  `}
                  />
                </button>
              </td>

              {/* Item Description */}
              <td className="px-2 py-3">
                <div
                  onClick={() => openDescriptionEditor(lang, idx)}
                  className="relative group/desc cursor-pointer"
                >
                  <input
                    readOnly
                    value={item["Item Description"] || ""}
                    placeholder="Add description..."
                    className="w-full bg-zinc-50/50 border border-transparent group-hover/desc:border-zinc-200 px-3 py-2 rounded-lg text-sm text-zinc-600 cursor-pointer transition-all truncate"
                  />
                  <div className="absolute inset-y-0 right-2 flex items-center opacity-0 group-hover/desc:opacity-100 transition-opacity">
                    <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded">EDIT</span>
                  </div>
                </div>
              </td>

              {/* Level 1 */}
              <td className="px-2 py-3">
                <input
                  value={item["Level 1"] || ""}
                  onChange={(e) => handleChange(lang, idx, "Level 1", e.target.value)}
                  className="w-full bg-transparent border border-transparent hover:border-zinc-200 focus:border-amber-500 px-3 py-2 rounded-lg text-sm"
                />
              </td>

              {/* Level 2 */}
              <td className="px-2 py-3">
                <input
                  value={item["Level 2"] || ""}
                  onChange={(e) => handleChange(lang, idx, "Level 2", e.target.value)}
                  className="w-full bg-transparent border border-transparent hover:border-zinc-200 focus:border-amber-500 px-3 py-2 rounded-lg text-sm"
                />
              </td>

              {/* Price */}
              <td className="px-2 py-3 w-28">
                <input
                  value={item["Price"] || ""}
                  onChange={(e) => handleChange(lang, idx, "Price", e.target.value)}
                  className="w-full text-right font-mono text-amber-700 bg-transparent border border-transparent hover:border-zinc-200 focus:border-amber-500 px-3 py-2 rounded-lg text-sm"
                />
              </td>

              {/* Category Select */}
              <td className="px-2 py-3 w-36">
                <div className="relative group">
                  <select
                    value={item.Veg_NonVeg || ""}
                    onChange={(e) => handleChange(lang, idx, "Veg_NonVeg", e.target.value)}
                    className="w-full bg-zinc-100/50 border border-transparent hover:border-zinc-200 hover:bg-white rounded-lg px-3 py-2 text-[11px] font-black uppercase tracking-wider text-zinc-600 transition-all outline-none appearance-none cursor-pointer"
                  >
                    <option value="">Select</option>
                    <option>Veg</option>
                    <option>NonVeg</option>
                    <option>Jain</option>
                    <option>drink</option>
                    <option>Taxi</option>
                    <option>Spa</option>
                    <option>Experience</option>
                    <option>Laundry</option>
                    <option>Other</option>
                  </select>

                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-400 group-hover:text-amber-500 transition-colors">
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </td>

              {/* Media Upload Management */}
              <td className="px-4 py-3 min-w-[200px]">
                <div className="flex items-center gap-3">
                  <div className="relative h-12 w-12 shrink-0 group/img">
                    <div className="h-full w-full rounded-xl overflow-hidden bg-zinc-100 border border-zinc-200">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} className="h-full w-full object-cover" alt="item" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-zinc-400">
                          <HiOutlinePhotograph size={20} />
                        </div>
                      )}
                    </div>

                    <label className="absolute inset-0 cursor-pointer bg-black/0 hover:bg-black/40 flex items-center justify-center transition-all rounded-xl">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e.target.files[0], lang, idx, item["Item Name"])}
                      />
                      <span className="text-[8px] text-white opacity-0 group-hover/img:opacity-100 font-bold uppercase tracking-tighter">Upload</span>
                    </label>
                  </div>

                  <div className="flex flex-col flex-1">
                    <div className="flex items-center gap-1 text-[9px] font-bold text-zinc-400 uppercase mb-1 tracking-widest">
                      <HiOutlineExternalLink size={10} /> Image Path
                    </div>
                    <input
                      readOnly
                      value={item.imageUrl || ""}
                      className="w-full bg-transparent text-[10px] text-zinc-400 italic outline-none truncate"
                    />
                  </div>
                </div>
              </td>

              {/* Actions */}
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => deleteDishRow(lang, idx)}
                  className="p-2 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all duration-200"
                >
                  <HiOutlineTrash size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ⬆ imports remain same

  // ⬇ replace only return JSX with this

  if (loading) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">

      {/* 🔒 Sticky Top Navbar */}
      <div className="sticky top-0 z-40 bg-white border-b shadow-sm px-6 py-3 flex flex-wrap items-center justify-between gap-3">

        <div className="flex items-center gap-2">
          {/* Add Item */}
          <div className="relative group">
            <button
              onClick={() => addDishRow(activeTab)}
              className="icon-btn"
            >
              <FaPlus />
            </button>
            <span className="tooltip">Add Item</span>
          </div>

          {/* Save Changes */}
          <div className="relative group">
            <button
              onClick={() => setShowSummary(true)}
              className="icon-btn"
            >
              <FaSave />
            </button>
            <span className="tooltip">Save Changes</span>
          </div>

          {/* Copy English Images */}
          <div className="relative group">
            <button
              onClick={syncEnglishImagesToHindi}
              className="icon-btn"
            >
              <FaSyncAlt />
            </button>
            <span className="tooltip">Copy English Images → Indian</span>
          </div>

          {/* View Menu */}
          <div className="relative group">
            <button
              onClick={() =>
                window.open(`https://zafflo.com/menu/${slug}`, "_blank")
              }
              className="icon-btn"
            >
              <FaEye />
            </button>
            <span className="tooltip">View Your Menu</span>
          </div>

          {/* Generate QR */}
          <div className="relative group">
            <button
              onClick={() => setOpenQrDrawer(true)}
              className="icon-btn"
            >
              <FaQrcode />
            </button>
            <span className="tooltip">Generate QR Codes</span>
          </div>

          {/* Working Hours */}
          <div className="relative group">
            <button
              onClick={() => setOpenWorkingHoursDrawer(true)}
              className="icon-btn"
            >
              <FaClock />
            </button>
            <span className="tooltip">Set Working Hours</span>
          </div>
          {/* Staff Management */}
          <div className="relative group">
            <button
              onClick={() => setShowStaffDrawer(true)}
              className="icon-btn"
            >
              <FaUsers />
            </button>
            <span className="tooltip">Manage Staff</span>
          </div>
        </div>




        {openQrDrawer && (
          <QrDrawer
            onClose={() => setOpenQrDrawer(false)}
            hotelName={userDetails.businessName}
            logo={userDetails.logo}
            slug={createSlug(userDetails.businessName)}
            businessType={userDetails.businessType}
            businessSubType={userDetails.businessSubType}
          />
        )}

        {openWorkingHoursDrawer && (
          <WorkingHoursDrawer
            onClose={() => setOpenWorkingHoursDrawer(false)}
            userId={userDetails.email}
          />
        )}

        {showStaffDrawer && (
          <StaffDrawer
            onClose={() => setShowStaffDrawer(false)}
            userId={userDetails.email}
          />
        )}

        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("english")}
            className={`px-4 py-2 rounded-lg ${activeTab === "english" ? "bg-[#6cbb23] text-white" : "bg-gray-100"
              }`}
          >
            ENGLISH
          </button>

          <button
            onClick={() => setActiveTab("hindi")}
            className={`px-4 py-2 rounded-lg ${activeTab === "hindi" ? "bg-[#6cbb23] text-white" : "bg-gray-100"
              }`}
          >
            INDIAN
          </button>
        </div>

      </div>

      {/* ⚠ Unsaved warning */}
      {isDirty && (
        <div className="mx-6 mt-3 bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-2 rounded-lg">
          ⚠ You have unsaved changes
        </div>
      )}

      {/* 📊 Grid */}
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-md overflow-x-auto">
          {renderTable(activeTab)}
        </div>
      </div>

      {/* 🧾 Change Summary Modal */}
      {showSummary && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-xl p-6 space-y-4 shadow-xl">
            <h2 className="text-lg font-bold text-gray-800">Review Changes</h2>

            <ul className="max-h-64 overflow-y-auto text-sm space-y-1">
              {generateSummary().map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowSummary(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => { handleSaveMenu(); setShowSummary(false); }}
                className="px-4 py-2 bg-[#6cbb23] text-white rounded-lg shadow"
              >
                Confirm Save
              </button>
            </div>
          </div>
        </div>
      )}


      {descEditor.open && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
          onClick={() => setDescEditor(EMPTY_DESC_EDITOR)}
        >
          <div
            className="bg-white w-full max-w-2xl rounded-xl shadow-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Edit Item Description
            </h2>

            {/* Toolbar */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => document.execCommand("bold")}
                className="px-3 py-1 border rounded font-bold hover:bg-gray-100"
              >
                B
              </button>

              <button
                type="button"
                onClick={() => document.execCommand("italic")}
                className="px-3 py-1 border rounded italic hover:bg-gray-100"
              >
                I
              </button>
            </div>

            {/* Editor */}
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onPaste={(e) => {
                e.preventDefault();
                const text = e.clipboardData.getData("text/plain");
                document.execCommand("insertText", false, text);
              }}
              className="border rounded-lg p-3 min-h-[160px] 
                   focus:ring-2 focus:ring-[#6cbb23]/30 outline-none"
              style={{
                fontFamily: "Calibri, sans-serif",
                fontSize: "12px",
              }}
            />

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setDescEditor(EMPTY_DESC_EDITOR)}
                className="px-4 py-2 rounded-lg bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  const html = editorRef.current?.innerHTML || "";

                  // 🔒 SANITIZE HTML
                  const tempDiv = document.createElement("div");
                  tempDiv.innerHTML = html;

                  tempDiv.querySelectorAll("*").forEach((el) => {
                    // Remove all inline styles
                    el.removeAttribute("style");

                    // Remove unwanted tags except b, strong, i, em, br
                    const allowedTags = ["B", "STRONG", "I", "EM", "BR"];
                    if (!allowedTags.includes(el.tagName)) {
                      const span = document.createElement("span");
                      span.innerHTML = el.innerHTML;
                      el.replaceWith(span);
                    }
                  });

                  const cleanedHTML = tempDiv.innerHTML;

                  handleChange(
                    descEditor.lang,
                    descEditor.idx,
                    "Item Description",
                    cleanedHTML
                  );

                  setDescEditor(EMPTY_DESC_EDITOR);
                }}
                className="px-4 py-2 rounded-lg bg-[#6cbb23] text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );

}
